// src/frontend/src/pages/TeamAdmin.tsx - Fixed version
import React, { useState, useEffect } from 'react';
import Layout from '../components/Layout/Layout';
import api from '../api/api';
import { Link } from 'react-router-dom';

interface TeamMember {
  _id: string;
  name: string;
  email: string;
  creditsUsed: number;
  averageScore: number;
  lastActive: string;
  status: 'active' | 'invited' | 'inactive';
}

interface InviteData {
  email: string;
  name: string;
}

const TeamAdmin: React.FC = () => {
  const [teamData, setTeamData] = useState<any>(null);
  const [members, setMembers] = useState<TeamMember[]>([]);
  const [leaderboard, setLeaderboard] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<'overview' | 'members' | 'leaderboard'>('overview');
  
  // Invite modal state
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [inviteData, setInviteData] = useState<InviteData>({ email: '', name: '' });
  const [isInviting, setIsInviting] = useState(false);
  const [inviteError, setInviteError] = useState('');
  const [inviteSuccess, setInviteSuccess] = useState('');

  useEffect(() => {
    fetchTeamData();
  }, []);

  // FIXED: Corrected fetchTeamData function with proper API endpoints
  const fetchTeamData = async () => {
    try {
      setIsLoading(true);
      
      // Use the correct API endpoints that match your backend
      const [dashboardResponse, leaderboardResponse] = await Promise.all([
        api.get('/teams/dashboard'),        // ✅ This exists in backend
        api.get('/teams/leaderboard')       // ✅ This exists in backend
      ]);
      
      // Extract data from dashboard response
      const dashboardData = dashboardResponse.data.data;
      
      // Set team data from dashboard
      setTeamData({
        name: dashboardData.team.name,
        organization: dashboardData.team.organization,
        seatCount: dashboardData.teamTotals.totalSeats,
        creditsPerSeat: dashboardData.teamTotals.totalCreditsAvailable / dashboardData.teamTotals.totalSeats,
        expiresAt: dashboardData.teamTotals.expiresAt,
        isOwner: dashboardData.team.isOwner
      });
      
      // Set members from dashboard memberStats
      const formattedMembers = dashboardData.memberStats.map((member: any) => ({
        _id: member._id,
        name: member.name,
        email: member.email,
        creditsUsed: member.creditsUsed,
        averageScore: member.averageScore,
        lastActive: new Date().toISOString(), // You might want to add this to backend
        status: 'active' // You might want to add this to backend
      }));
      setMembers(formattedMembers);
      
      // Set leaderboard data
      setLeaderboard(leaderboardResponse.data.data.leaderboard);
      
    } catch (err: any) {
      console.error('Failed to fetch team data:', err);
      
      // Check if it's a 403 error (not authorized)
      if (err.response?.status === 403) {
        console.log('Not authorized for team features');
        // Don't set teamData, which will show the "no access" message
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleInviteTeamMember = async (e: React.FormEvent) => {
    e.preventDefault();
    setInviteError('');
    setInviteSuccess('');
    setIsInviting(true);

    try {
      await api.post('/teams/invite', inviteData);
      setInviteSuccess(`Invitation sent to ${inviteData.email}`);
      setInviteData({ email: '', name: '' });
      
      // Refresh team data
      fetchTeamData();
      
      // Close modal after 2 seconds
      setTimeout(() => {
        setShowInviteModal(false);
        setInviteSuccess('');
      }, 2000);
    } catch (err: any) {
      setInviteError(err.response?.data?.message || 'Failed to send invitation');
    } finally {
      setIsInviting(false);
    }
  };

  const handleRemoveMember = async (memberId: string) => {
    if (!window.confirm('Are you sure you want to remove this team member?')) {
      return;
    }

    try {
      await api.delete(`/teams/members/${memberId}`);
      // Refresh team data
      fetchTeamData();
    } catch (err: any) {
      alert(err.response?.data?.message || 'Failed to remove team member');
    }
  };

  // FIXED: Updated export function to use correct endpoint
  const handleExportCSV = async () => {
    try {
      const response = await api.get('/teams/report/download', { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `team-report-${new Date().toISOString().split('T')[0]}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (err) {
      console.error('Failed to export CSV:', err);
      alert('Failed to export CSV. Please try again.');
    }
  };

  const handleDownloadReport = async () => {
    try {
      const response = await api.get('/teams/report/pdf', { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `team-rollup-report.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (err) {
      console.error('Failed to download report:', err);
    }
  };

  if (isLoading) {
    return (
      <Layout>
        <div className="text-center py-8">
          <p>Loading team data...</p>
        </div>
      </Layout>
    );
  }

  if (!teamData) {
    return (
      <Layout>
        <div className="text-center py-8">
          <p className="text-gray-600">You don't have access to team admin features.</p>
          <Link to="/dashboard" className="text-blue-600 hover:underline mt-2">
            Go to Dashboard
          </Link>
        </div>
      </Layout>
    );
  }

  const totalCreditsUsed = members.reduce((sum, member) => sum + member.creditsUsed, 0);
  const totalCredits = teamData.seatCount * teamData.creditsPerSeat;
  const daysRemaining = Math.max(0, Math.ceil((new Date(teamData.expiresAt).getTime() - Date.now()) / (1000 * 60 * 60 * 24)));
  const seatsAvailable = teamData.seatCount - members.filter(m => m.status === 'active' || m.status === 'invited').length;

  return (
    <Layout>
      <div className="max-w-7xl mx-auto">
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-2xl font-bold">Team Admin Dashboard</h1>
          <div className="flex gap-2">
            <button
              onClick={() => setShowInviteModal(true)}
              disabled={seatsAvailable === 0}
              className={`px-4 py-2 rounded flex items-center ${
                seatsAvailable > 0 
                  ? 'bg-green-600 text-white hover:bg-green-700'
                  : 'bg-gray-300 text-gray-500 cursor-not-allowed'
              }`}
            >
              <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              Invite Member
            </button>
            <button
              onClick={handleExportCSV}
              className="px-4 py-2 bg-gray-100 text-gray-700 rounded hover:bg-gray-200"
            >
              Export CSV
            </button>
            <button
              onClick={handleDownloadReport}
              className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
            >
              Download Report
            </button>
          </div>
        </div>

        {/* Key Metrics */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <div className="bg-white p-6 rounded-lg shadow">
            <h3 className="text-sm font-medium text-gray-500">Active Seats</h3>
            <p className="text-2xl font-bold text-gray-900">
              {members.filter(m => m.status === 'active').length} / {teamData.seatCount}
            </p>
            {seatsAvailable > 0 && (
              <p className="text-sm text-green-600 mt-1">{seatsAvailable} available</p>
            )}
          </div>
          <div className="bg-white p-6 rounded-lg shadow">
            <h3 className="text-sm font-medium text-gray-500">Credits Used</h3>
            <p className="text-2xl font-bold text-gray-900">{totalCreditsUsed} / {totalCredits}</p>
          </div>
          <div className="bg-white p-6 rounded-lg shadow">
            <h3 className="text-sm font-medium text-gray-500">Avg Team Score</h3>
            <p className="text-2xl font-bold text-gray-900">
              {members.length > 0 
                ? (members.reduce((sum, m) => sum + m.averageScore, 0) / members.length).toFixed(1)
                : '0.0'}/10
            </p>
          </div>
          <div className="bg-white p-6 rounded-lg shadow">
            <h3 className="text-sm font-medium text-gray-500">Days Remaining</h3>
            <p className="text-2xl font-bold text-gray-900">{daysRemaining}</p>
            {daysRemaining <= 7 && (
              <p className="text-sm text-red-600 mt-1">Expires soon!</p>
            )}
          </div>
        </div>

        {/* Tabs */}
        <div className="bg-white rounded-lg shadow">
          <div className="border-b border-gray-200">
            <nav className="-mb-px flex">
              <button
                onClick={() => setActiveTab('overview')}
                className={`py-2 px-6 border-b-2 font-medium text-sm ${
                  activeTab === 'overview'
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
              >
                Overview
              </button>
              <button
                onClick={() => setActiveTab('members')}
                className={`py-2 px-6 border-b-2 font-medium text-sm ${
                  activeTab === 'members'
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
              >
                Team Members
              </button>
              <button
                onClick={() => setActiveTab('leaderboard')}
                className={`py-2 px-6 border-b-2 font-medium text-sm ${
                  activeTab === 'leaderboard'
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
              >
                Leaderboard
              </button>
            </nav>
          </div>

          <div className="p-6">
            {activeTab === 'overview' && (
              <div>
                <h2 className="text-lg font-medium mb-4">Team Overview</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <h3 className="font-medium text-gray-700 mb-2">Usage Trends</h3>
                    <div className="bg-gray-50 p-4 rounded">
                      <p className="text-sm text-gray-600">
                        Your team has completed {totalCreditsUsed} simulations with an average score of{' '}
                        {members.length > 0 
                          ? (members.reduce((sum, m) => sum + m.averageScore, 0) / members.length).toFixed(1)
                          : '0.0'}/10.
                      </p>
                    </div>
                  </div>
                  <div>
                    <h3 className="font-medium text-gray-700 mb-2">Top Performers</h3>
                    <div className="space-y-2">
                      {leaderboard.slice(0, 3).map((member, index) => (
                        <div key={member._id} className="flex items-center justify-between bg-gray-50 p-2 rounded">
                          <span className="text-sm">
                            {index + 1}. {member.name}
                          </span>
                          <span className="text-sm font-medium">{member.averageScore.toFixed(1)}/10</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'members' && (
              <div>
                <div className="flex justify-between items-center mb-4">
                  <h2 className="text-lg font-medium">Team Members</h2>
                  <p className="text-sm text-gray-600">
                    {members.filter(m => m.status === 'invited').length} pending invitations
                  </p>
                </div>
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead>
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Name</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Email</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Credits Used</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Avg Score</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Last Active</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {members.map((member) => (
                        <tr key={member._id}>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                            {member.name}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{member.email}</td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className={`inline-flex px-2 py-1 text-xs rounded-full ${
                              member.status === 'active' 
                                ? 'bg-green-100 text-green-800'
                                : member.status === 'invited'
                                ? 'bg-yellow-100 text-yellow-800'
                                : 'bg-gray-100 text-gray-800'
                            }`}>
                              {member.status}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {member.creditsUsed}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {member.averageScore.toFixed(1)}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {new Date(member.lastActive).toLocaleDateString()}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm">
                            <button
                              onClick={() => handleRemoveMember(member._id)}
                              className="text-red-600 hover:text-red-900"
                            >
                              Remove
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {activeTab === 'leaderboard' && (
              <div>
                <h2 className="text-lg font-medium mb-4">Team Leaderboard</h2>
                <div className="max-w-2xl mx-auto">
                  {leaderboard.map((member, index) => (
                    <div key={member._id} className={`flex items-center justify-between p-4 rounded-lg mb-2 ${
                      index === 0 ? 'bg-yellow-50 border border-yellow-200' :
                      index === 1 ? 'bg-gray-100 border border-gray-200' :
                      index === 2 ? 'bg-orange-50 border border-orange-200' :
                      'bg-gray-50'
                    }`}>
                      <div className="flex items-center">
                        <span className="text-2xl font-bold mr-4 text-gray-600">
                          {index + 1}
                        </span>
                        <div>
                          <p className="font-medium text-gray-900">{member.name}</p>
                          <p className="text-sm text-gray-500">
                            {member.completedSimulations} simulations completed
                          </p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="text-2xl font-bold text-gray-900">{member.averageScore.toFixed(1)}</p>
                        <p className="text-sm text-gray-500">avg score</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Invite Member Modal */}
      {showInviteModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-md">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-medium">Invite Team Member</h3>
              <button
                onClick={() => setShowInviteModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                </svg>
              </button>
            </div>

            {inviteError && (
              <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
                {inviteError}
              </div>
            )}

            {inviteSuccess && (
              <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded mb-4">
                {inviteSuccess}
              </div>
            )}

            <form onSubmit={handleInviteTeamMember}>
              <div className="mb-4">
                <label htmlFor="name" className="block text-gray-700 font-medium mb-2">
                  Name
                </label>
                <input
                  type="text"
                  id="name"
                  value={inviteData.name}
                  onChange={(e) => setInviteData({ ...inviteData, name: e.target.value })}
                  className="w-full border border-gray-300 rounded-md py-2 px-3 focus:outline-none focus:ring focus:border-blue-300"
                  required
                />
              </div>

              <div className="mb-6">
                <label htmlFor="email" className="block text-gray-700 font-medium mb-2">
                  Email
                </label>
                <input
                  type="email"
                  id="email"
                  value={inviteData.email}
                  onChange={(e) => setInviteData({ ...inviteData, email: e.target.value })}
                  className="w-full border border-gray-300 rounded-md py-2 px-3 focus:outline-none focus:ring focus:border-blue-300"
                  required
                />
              </div>

              <div className="flex justify-end gap-3">
                <button
                  type="button"
                  onClick={() => setShowInviteModal(false)}
                  className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isInviting}
                  className={`px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 ${
                    isInviting ? 'opacity-50 cursor-not-allowed' : ''
                  }`}
                >
                  {isInviting ? 'Sending...' : 'Send Invitation'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </Layout>
  );
};

export default TeamAdmin;